import apiClient from '@/api/apiClient';
import { handleError, getAuthData, prepareModel, validateId } from '../../utils/storeHelpers';

export default {
  namespaced: true,
  state: {
    schema: {},
    catDiagnosticICD10: [],
    catDiagnosticICD11: [],
    catDiagnosticDSM5: [],
    dataCrud: [],
    checkboxNone: 0,
    isNewRecord: true,
    status: '',
  },

  getters: {
    schema: (state) => state.schema,
    catDiagnosticICD10: (state) => state.catDiagnosticICD10,
    catDiagnosticICD11: (state) => state.catDiagnosticICD11,
    catDiagnosticDSM5: (state) => state.catDiagnosticDSM5,
    dataCrud: (state) => state.dataCrud,
    checkboxNone: (state) => state.checkboxNone,
    isNewRecord: (state) => state.isNewRecord,
    status: (state) => state.status,
  },

  mutations: {
    SET_SCHEMA(state, payload) {
      state.schema = payload?.schema;
      state.catDiagnosticICD10 = Array.isArray(payload?.catDiagnosticICD10)
        ? payload.catDiagnosticICD10
        : [];
      state.catDiagnosticICD11 = Array.isArray(payload?.catDiagnosticICD11)
        ? payload.catDiagnosticICD11
        : [];
      state.catDiagnosticDSM5 = Array.isArray(payload?.catDiagnosticDSM5)
        ? payload.catDiagnosticDSM5
        : [];
    },
    SET_RECORDS(state, { recordsData }) {
      state.dataCrud = Array.isArray(recordsData?.dataCrud) ? recordsData.dataCrud : [];
      state.checkboxNone = recordsData?.checkboxNone;
      state.isNewRecord = Boolean(recordsData?.isNewRecord);
    },
    SET_STATUS(state, status) {
      state.status = status;
    },
  },

  actions: {
    async fetchSchema({ commit }) {
      try {
        commit('SET_STATUS', 'loading');
        const responses = await Promise.all([
          apiClient.anxAFxFamilyMembersSchema(),
          apiClient.catBond(),
          apiClient.catBirthSexes(),
          apiClient.catGenders(),
          apiClient.catSexOrientation(),
          apiClient.catDevelopmentStages(),
          apiClient.catISO3366_1(),
          apiClient.catDepartments(),
          apiClient.catProvinces(),
          apiClient.catDistricts(),
          apiClient.catOccupation(),
          apiClient.catAgeInterval(),
          apiClient.catDeathCauses(),
          apiClient.catDiagnosticManual(),
          apiClient.catDiagnosticICD10(),
          apiClient.catDiagnosticICD11(),
          apiClient.catDiagnosticDSM5(),
          apiClient.catPersonTrait(),
          apiClient.catCoupleFamiliarity(),
        ]);
        const [
          schemaRes,
          catBond,
          catBirthSexes,
          catGenders,
          catSexOrientation,
          catDevelopmentStages,
          catISO3366_1,
          catDepartments,
          catProvinces,
          catDistricts,
          catOccupation,
          catAgeInterval,
          catDeathCauses,
          catDiagManual,
          catDiagICD10,
          catDiagICD11,
          catDiagDSM5,
          catPersonTrait,
          catCoupleFamiliarity,
        ] = responses.map((res) => res?.data ?? {});

        let id1 = 'G';
        const resCat18 = await apiClient.catLevelDescription(id1);
        const catLevelDescriptionG = resCat18.data.data;

        let catDiagnosticManualPre = catDiagManual.data ?? [];
        const catDiagnosticManual = JSON.stringify(catDiagnosticManualPre);

        let catDiagManualDisease = JSON.parse(catDiagnosticManual);
        catDiagManualDisease[0].type = 'disease';
        catDiagManualDisease[1].type = 'disease';
        catDiagManualDisease.splice(2, 1);

        let catDiagManualMental = JSON.parse(catDiagnosticManual);
        catDiagManualMental[0].type = 'mental';
        catDiagManualMental[1].type = 'mental';
        catDiagManualMental[2].type = 'mental';

        const catDiagnosticICD10 = catDiagICD10.data ?? [];
        const catDiagnosticICD11 = catDiagICD11.data ?? [];
        const catDiagnosticDSM5 = catDiagDSM5.data ?? [];

        let chapterVal = '06';
        let catDiagnosticICD11Mental = catDiagnosticICD11;
        catDiagnosticICD11Mental = catDiagnosticICD11Mental.filter((item) =>
          item.diagnostic_icd_11_chapter.includes(chapterVal)
        );

        if (!schemaRes.formCrud) throw new Error('Estructura de schema inválida.');

        const schema = structuredClone(schemaRes);
        schema.formCrud.bond.items = catBond.data ?? [];
        schema.formCrud.back_fam_birth_sexes_id.items = catBirthSexes.data ?? [];
        schema.formCrud.back_fam_genders_id.items = catGenders.data ?? [];
        schema.formCrud.back_fam_sex_orientation_id.items = catSexOrientation.data ?? [];
        schema.formCrud.back_fam_development_stages.items = catDevelopmentStages.data ?? [];
        schema.formCrud.back_fam_relation_with_patient_level_description_id.items =
          catLevelDescriptionG;
        schema.formCrud.back_fam_place_origin_iso3366.items = catISO3366_1.data ?? [];
        schema.formCrud.back_fam_place_origin_department.items = catDepartments.data ?? [];
        schema.formCrud.back_fam_place_origin_province.items = catProvinces.data ?? [];
        schema.formCrud.back_fam_place_origin_district.items = catDistricts.data ?? [];
        schema.formCrud.back_fam_member_occupation.items = catOccupation.data ?? [];
        schema.formCrud.back_fam_death_age.items = catAgeInterval.data ?? [];
        schema.formCrud.back_fam_death_causes_id.items = catDeathCauses.data ?? [];
        schema.formCrud.back_fam_disease_diagnostic_manual.items = catDiagManualDisease;
        schema.formCrud.back_fam_member_disease.items = catDiagnosticICD11;
        schema.formCrud.back_fam_mental_diagnostic_manual.items = catDiagManualMental;
        schema.formCrud.back_fam_member_mental_problem.items = catDiagnosticICD11Mental;
        schema.formCrud.back_fam_member_trait.items = catPersonTrait.data ?? [];
        schema.formCrud.i1_back_fam_couple_familiarity.items = catCoupleFamiliarity.data ?? [];
        schema.formCrud.i1_back_fam_relation_with_couple_id.items = catLevelDescriptionG;
        schema.formCrud.i1_back_fam_couple_relation_with_son_1_id.items = catLevelDescriptionG;
        schema.formCrud.i1_back_fam_couple_relation_with_son_2_id.items = catLevelDescriptionG;
        schema.formCrud.i1_back_fam_couple_relation_with_son_3_id.items = catLevelDescriptionG;
        schema.formCrud.i1_back_fam_couple_relation_with_son_4_id.items = catLevelDescriptionG;
        schema.formCrud.i1_back_fam_couple_relation_with_son_5_id.items = catLevelDescriptionG;
        schema.formCrud.i1_back_fam_couple_relation_with_son_6_id.items = catLevelDescriptionG;
        schema.formCrud.i1_back_fam_couple_relation_with_son_7_id.items = catLevelDescriptionG;
        schema.formCrud.i1_back_fam_couple_relation_with_son_8_id.items = catLevelDescriptionG;
        schema.formCrud.i2_back_fam_couple_familiarity.items = catCoupleFamiliarity.data ?? [];
        schema.formCrud.i2_back_fam_relation_with_couple_id.items = catLevelDescriptionG;
        schema.formCrud.i2_back_fam_couple_relation_with_son_1_id.items = catLevelDescriptionG;
        schema.formCrud.i2_back_fam_couple_relation_with_son_2_id.items = catLevelDescriptionG;
        schema.formCrud.i2_back_fam_couple_relation_with_son_3_id.items = catLevelDescriptionG;
        schema.formCrud.i2_back_fam_couple_relation_with_son_4_id.items = catLevelDescriptionG;
        schema.formCrud.i2_back_fam_couple_relation_with_son_5_id.items = catLevelDescriptionG;
        schema.formCrud.i2_back_fam_couple_relation_with_son_6_id.items = catLevelDescriptionG;
        schema.formCrud.i2_back_fam_couple_relation_with_son_7_id.items = catLevelDescriptionG;
        schema.formCrud.i2_back_fam_couple_relation_with_son_8_id.items = catLevelDescriptionG;
        schema.formCrud.i3_back_fam_couple_familiarity.items = catCoupleFamiliarity.data ?? [];
        schema.formCrud.i3_back_fam_relation_with_couple_id.items = catLevelDescriptionG;
        schema.formCrud.i3_back_fam_couple_relation_with_son_1_id.items = catLevelDescriptionG;
        schema.formCrud.i3_back_fam_couple_relation_with_son_2_id.items = catLevelDescriptionG;
        schema.formCrud.i3_back_fam_couple_relation_with_son_3_id.items = catLevelDescriptionG;
        schema.formCrud.i3_back_fam_couple_relation_with_son_4_id.items = catLevelDescriptionG;
        schema.formCrud.i3_back_fam_couple_relation_with_son_5_id.items = catLevelDescriptionG;
        schema.formCrud.i3_back_fam_couple_relation_with_son_6_id.items = catLevelDescriptionG;
        schema.formCrud.i3_back_fam_couple_relation_with_son_7_id.items = catLevelDescriptionG;
        schema.formCrud.i3_back_fam_couple_relation_with_son_8_id.items = catLevelDescriptionG;

        commit('SET_SCHEMA', {
          schema,
          catDiagnosticICD10,
          catDiagnosticICD11,
          catDiagnosticDSM5,
        });
      } catch (error) {
        handleError(error, commit);
      }
    },

    async fetchRecords({ commit, rootState }) {
      try {
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        const res = await apiClient.modelANxAFxFamilyMembers(clinicalHistories_id, institutions_profes_id);
        const recordsData = res?.data?.data ?? [];
        commit('SET_RECORDS', { recordsData });
        commit('SET_STATUS', 'loaded');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async createRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'create');
        const preparedModel = prepareModel(rootState, model);
        await apiClient.createANxAFxFamilyMembers(preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async updateRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'update');
        const { clinicalHistories_id } = getAuthData(rootState);
        const preparedModel = prepareModel(rootState, model);
        await apiClient.updateANxAFxFamilyMembers(clinicalHistories_id, preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async removeRecord({ commit, rootState }, { id }) {
      try {
        commit('SET_STATUS', 'remove');
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        validateId(id);
        await apiClient.deleteANxAFxFamilyMembers(id, clinicalHistories_id, institutions_profes_id);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },
  },
};
